import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { colors } from '../../styles/theme';
import { withAlpha } from '../../Map/utils/sharedBranding';
import { PUBLICATION_BLOCK_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import {
  showroomCountsRowSx,
  showroomFormFooterSx,
  showroomFormGroupDividerSx,
  showroomScrollSx,
  showroomToolbarSx,
} from '../Showroom.styles';

const skBarSx = { bgcolor: colors.border, borderRadius: '8px' };
const skLineSx = { bgcolor: colors.border, borderRadius: '4px' };
const skMutedSx = {
  bgcolor: withAlpha(colors.textPanelTitle, 0.16),
  borderRadius: '4px',
};
const skChipSx = {
  bgcolor: withAlpha(colors.primary, 0.24),
  borderRadius: '4px',
  flexShrink: 0,
};

const skeletonCardSx = {
  px: 1.5,
  py: 1.25,
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
  bgcolor: colors.backgroundDark,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
};

/**
 * Placeholder del listado CAD mientras carga el GET, alineado al detalle de lista.
 */
export default function ShowroomParcelListSkeleton({ count = 7 }) {
  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      aria-busy="true"
      aria-label="Cargando lista"
    >
      <Box sx={showroomToolbarSx}>
        <Skeleton variant="rounded" height={44} sx={skBarSx} />
        <Box sx={showroomCountsRowSx}>
          <Skeleton variant="rounded" width={132} height={14} sx={skLineSx} />
          <Skeleton variant="rounded" width={148} height={14} sx={skLineSx} />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          px: 2.5,
          py: 1.5,
        }}
      >
        {Array.from({ length: count }).map((_, idx) => (
          <Box key={idx} sx={skeletonCardSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
              <Skeleton
                variant="rounded"
                width={`${52 - (idx % 3) * 8}%`}
                height={16}
                sx={skLineSx}
              />
              <Skeleton
                variant="rounded"
                width={idx % 2 === 0 ? 88 : 78}
                height={22}
                sx={skChipSx}
              />
            </Box>
            <Skeleton
              variant="rounded"
              width={`${38 - (idx % 3) * 4}%`}
              height={12}
              sx={skMutedSx}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function FormFieldSkeleton({ height = 40, width = '100%' }) {
  return (
    <Box>
      <Skeleton variant="rounded" width={88} height={10} sx={{ ...skMutedSx, mb: 0.75 }} />
      <Skeleton variant="rounded" width={width} height={height} sx={skBarSx} />
    </Box>
  );
}

/**
 * Placeholder del formulario de parcela CAD (mismo chrome que Publicación).
 */
export function ShowroomParcelFormSkeleton() {
  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      aria-busy="true"
      aria-label="Cargando lote"
    >
      <Box sx={showroomScrollSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5, pb: 1 }}>
          <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Skeleton variant="circular" width={16} height={16} sx={skMutedSx} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="rounded" width={92} height={12} sx={skLineSx} />
                <Skeleton variant="rounded" width="62%" height={10} sx={{ ...skMutedSx, mt: 0.75 }} />
              </Box>
            </Box>
            <FormFieldSkeleton />
            <FormFieldSkeleton height={88} />
            <Box sx={showroomFormGroupDividerSx}>
              <Skeleton variant="rounded" width={64} height={12} sx={{ ...skLineSx, mb: 1.25 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <FormFieldSkeleton />
                <FormFieldSkeleton />
              </Box>
            </Box>
            <Box sx={showroomFormGroupDividerSx}>
              <Skeleton variant="rounded" width={56} height={12} sx={{ ...skLineSx, mb: 1.25 }} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ flex: '1.5 1 140px', minWidth: 0 }}>
                  <FormFieldSkeleton />
                </Box>
                <Box sx={{ flex: '1 1 100px', minWidth: 0 }}>
                  <FormFieldSkeleton />
                </Box>
              </Box>
            </Box>
            <Box sx={showroomFormGroupDividerSx}>
              <Skeleton variant="rounded" width={72} height={12} sx={{ ...skLineSx, mb: 1.25 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <FormFieldSkeleton />
                <FormFieldSkeleton />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                <FormFieldSkeleton />
                <FormFieldSkeleton />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={showroomFormFooterSx}>
        <Skeleton variant="rounded" width={112} height={40} sx={skBarSx} />
      </Box>
    </Box>
  );
}
